/**
 * The address of the page «Новый заказ» ⇄ the «когда и где» of step 1 (design §5.3). The query is snake_case: `brand_id` (only when it is not the
 * default brand), `city_id`, `date` (only when it is not the city's today), `d` — the id of the draft (six random characters: nothing personal), `held_cart_id` — the booking
 * of the operator's own that is being changed («Изменить» of «Когда и где» on step 2): its pieces and its slot are not counted as taken.
 * The phone (`phone`) and the client (`client_id`) the page may be opened with are read ONCE and taken out of the address at once
 * (`replaceUrl`): the number of a client must not stay in the browser's history. Pure.
 */

export interface NewOrderUrlState {
  brandId?: number;
  cityId?: number;
  /** `yyyy-MM-dd`. */
  date?: string;
  /** The draft's id. */
  draftId?: string;
  /** The booking that is held while the choice is changed (step 2 → step 1). */
  heldCartId?: number;
}

/** What the page was opened with — read once. */
export interface NewOrderEntry {
  /** Digits of the number as the caller wrote it (any format; the field normalises it). */
  phone?: string;
  clientId?: number;
}

export interface QueryLike {
  get(name: string): string | null;
}

/** The longest span of days the editor lets an order go ahead (E2 answers `dateInvalid` further). */
export const MAX_DAYS_AHEAD = 180;

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DRAFT_ID_RE = /^[A-Za-z0-9_-]{4,16}$/;

/** `yyyy-MM-dd` and a real calendar day (no «2026-02-31»). */
export function isIsoDate(value: string | null | undefined): value is string {
  const m = DATE_RE.exec(value ?? '');
  if (!m) {
    return false;
  }
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const probe = new Date(Date.UTC(y, mo - 1, d));
  return probe.getUTCFullYear() === y && probe.getUTCMonth() === mo - 1 && probe.getUTCDate() === d;
}

export function isDraftId(value: string | null | undefined): value is string {
  return DRAFT_ID_RE.test(value ?? '');
}

function positiveInt(value: string | null): number | undefined {
  return value !== null && /^\d{1,9}$/.test(value) && Number(value) > 0 ? Number(value) : undefined;
}

/** The number of a booking: whole digits, as long as the booking numbers of the server are (`/order/new/981200001`). */
function bookingId(value: string | null): number | undefined {
  return value !== null && /^\d{1,15}$/.test(value) && Number(value) > 0 ? Number(value) : undefined;
}

export function parseNewOrderUrl(query: QueryLike): { state: NewOrderUrlState; entry: NewOrderEntry } {
  const date = query.get('date');
  const draftId = query.get('d');
  const brandId = positiveInt(query.get('brand_id'));
  const cityId = positiveInt(query.get('city_id'));
  const heldCartId = bookingId(query.get('held_cart_id'));
  const phone = (query.get('phone') ?? '').replace(/\D/g, '');
  const clientId = positiveInt(query.get('client_id'));
  return {
    state: {
      ...(brandId !== undefined ? { brandId } : {}),
      ...(cityId !== undefined ? { cityId } : {}),
      ...(isIsoDate(date) ? { date } : {}),
      ...(isDraftId(draftId) ? { draftId } : {}),
      ...(heldCartId !== undefined ? { heldCartId } : {}),
    },
    entry: {
      ...(phone ? { phone: phone.slice(0, 15) } : {}),
      ...(clientId !== undefined ? { clientId } : {}),
    },
  };
}

/**
 * The query of the page for a state. The default brand and the city's today are not written (a link opened tomorrow should open on tomorrow's
 * today, and a link to the default brand should stay the short one). The entry parameters are never written.
 */
export function toNewOrderQuery(state: NewOrderUrlState, defaults: { defaultBrandId?: number; today?: string } = {}): Record<string, string> {
  const query: Record<string, string> = {};
  if (state.brandId !== undefined && state.brandId !== defaults.defaultBrandId) {
    query['brand_id'] = String(state.brandId);
  }
  if (state.cityId !== undefined) {
    query['city_id'] = String(state.cityId);
  }
  if (state.date && state.date !== defaults.today) {
    query['date'] = state.date;
  }
  if (state.draftId) {
    query['d'] = state.draftId;
  }
  if (state.heldCartId !== undefined) {
    query['held_cart_id'] = String(state.heldCartId);
  }
  return query;
}

/** The query as it is without the entry parameters — what `replaceUrl` navigates to right after they were read. */
export function withoutEntryParams(query: Record<string, unknown>): Record<string, unknown> {
  const rest = { ...query };
  delete rest['phone'];
  delete rest['client_id'];
  return rest;
}

/** The query carries an entry parameter the page has to read and take away. */
export function hasEntryParams(query: QueryLike): boolean {
  return query.get('phone') !== null || query.get('client_id') !== null;
}

// ---------- dates on the city's calendar ----------

/** `yyyy-MM-dd` + `days` — pure calendar arithmetic, no time zone anywhere. */
export function addDays(iso: string, days: number): string {
  const m = DATE_RE.exec(iso);
  if (!m) {
    return iso;
  }
  const at = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days));
  return `${at.getUTCFullYear()}-${String(at.getUTCMonth() + 1).padStart(2, '0')}-${String(at.getUTCDate()).padStart(2, '0')}`;
}

/** Whole days from `from` to `to` (negative when `to` is earlier). */
export function daysBetween(from: string, to: string): number {
  const a = DATE_RE.exec(from);
  const b = DATE_RE.exec(to);
  if (!a || !b) {
    return 0;
  }
  const ms = Date.UTC(Number(b[1]), Number(b[2]) - 1, Number(b[3])) - Date.UTC(Number(a[1]), Number(a[2]) - 1, Number(a[3]));
  return Math.round(ms / 86_400_000);
}

/**
 * The date the page may stand on: not before the city's today, not further than 180 days (and not beyond the last day with production limits
 * when the city has one) — anything else, or a missing date, is the city's today.
 */
export function clampDate(date: string | undefined, today: string, limitsUntil?: string): string {
  if (!date || !isIsoDate(date) || date < today) {
    return today;
  }
  const last = limitsUntil && isIsoDate(limitsUntil) && limitsUntil >= today ? limitsUntil : undefined;
  const bound = last && daysBetween(today, last) <= MAX_DAYS_AHEAD ? last : addDays(today, MAX_DAYS_AHEAD);
  return date > bound ? today : date;
}
